import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price, currency = 'EUR') {
  const currencyConfig = {
    EUR: { locale: 'nl-BE', currency: 'EUR' },
    USD: { locale: 'en-US', currency: 'USD' },
  };
  
  const config = currencyConfig[currency] || currencyConfig.EUR;
  
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
  }).format(price);
}

export function formatDate(date) {
  return new Date(date).toLocaleDateString('nl-BE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatTime(time) {
  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
}

export function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString('nl-BE', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
}

export function getStatusColor(status) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    ready: 'bg-green-100 text-green-800',
    delivered: 'bg-gray-100 text-gray-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    paid: 'bg-green-100 text-green-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getOrderTypeIcon(type) {
  const types = {
    delivery: '🚗',
    pickup: '🛍️',
    dine_in: '🍽️',
  };
  return types[type] || '📦';
}

export function getOrderTypeLabel(type, translations = {}) {
  const labels = {
    delivery: translations.delivery || 'Bezorging',
    pickup: translations.pickup || 'Afhalen',
    dine_in: translations.dine_in || 'Ter Plaatse',
  };
  return labels[type] || type;
}

export function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function truncateText(text, maxLength = 100) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// Get localized name based on language
export function getLocalizedName(item, language = 'nl') {
  const nameKey = `name_${language}`;
  return item[nameKey] || item.name_nl || item.name;
}

export function getLocalizedDescription(item, language = 'nl') {
  const descKey = `description_${language}`;
  return item[descKey] || item.description_nl || item.description;
}

// Format delivery address
export function formatDeliveryAddress(address) {
  if (!address) return '';
  const parts = [
    `${address.street} ${address.house_number}`,
    `${address.postal_code} ${address.city}`,
  ];
  if (address.additional_info) {
    parts.push(address.additional_info);
  }
  return parts.join(', ');
}

// Open print window for thermal printer (Windows/browser)
export function printOrderTicket(orderId) {
  const printUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/orders/${orderId}/print`;
  
  // Fetch and print via browser
  fetch(printUrl, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('restaurant_token')}`
    }
  })
  .then(res => res.json())
  .then(data => {
    // Create print window with receipt content
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <html>
      <head>
        <title>Bestelling ${data.order_id}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; }
          pre { white-space: pre-wrap; margin: 0; }
        </style>
      </head>
      <body>
        <pre>${data.receipt_text}</pre>
        <script>window.print(); setTimeout(() => window.close(), 500);</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  })
  .catch(err => console.error('Print error:', err));
}

// PushPrinter integration for Android
// Note: Pushprinter polls the /print/pending/{api_key} endpoint automatically
// This function triggers a manual print via the API
export async function printViaPushPrinter(orderId) {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/orders/${orderId}/print`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('restaurant_token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('Print job queued:', data);
      return data;
    }
  } catch (err) {
    console.error('PushPrinter error:', err);
    throw err;
  }
}

// Unified receipt printer used by Live Orders + Orders History.
// Mode is read from localStorage (same key the print-station toggle uses).
// Falls back gracefully: rawbt -> browser iframe -> popup-window.
export async function printReceipt(orderId, printerApiKey) {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  let mode = 'browser';
  try { mode = localStorage.getItem('foodflows_print_station_mode') || 'browser'; } catch { /* ignore */ }

  // RawBT silent print (Android)
  if (mode === 'rawbt' && printerApiKey) {
    try {
      const res = await fetch(`${baseUrl}/api/print/station/${printerApiKey}/rawbt/${orderId}`);
      if (res.ok) {
        const data = await res.json();
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = data.intent_url || data.rawbt_url;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => a.remove(), 1000);
        return { ok: true, mode: 'rawbt' };
      }
    } catch (e) { console.error('[printReceipt] rawbt failed', e); }
  }

  // Browser direct print via hidden iframe
  if (printerApiKey) {
    return new Promise((resolve) => {
      let iframe = document.getElementById('foodflows-print-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'foodflows-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
      }
      const url = `${baseUrl}/api/print/station/${printerApiKey}/receipt/${orderId}?t=${Date.now()}`;
      let done = false;
      const finish = (result) => { if (done) return; done = true; resolve(result); };
      const onLoad = () => {
        iframe.removeEventListener('load', onLoad);
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            finish({ ok: true, mode: 'browser' });
          } catch (e) {
            console.error('[printReceipt] browser print failed', e);
            finish({ ok: false, mode: 'browser' });
          }
        }, 400);
      };
      iframe.addEventListener('load', onLoad);
      setTimeout(() => finish({ ok: false, mode: 'browser' }), 10000);
      iframe.src = url;
    });
  }

  // Fallback — no printer api key available
  printOrderTicket(orderId);
  return { ok: true, mode: 'popup' };
}
